package org.gstechnovos.jexonplastech.sections.home.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.gstechnovos.jexonplastech.sections.home.styles.CompanyOverviewContainerStyle
import org.gstechnovos.jexonplastech.sections.home.styles.StatCardStyle
import org.gstechnovos.jexonplastech.theme.toSitePalette
import org.gstechnovos.jexonplastech.theme.unextractable
import org.gstechnovos.jexonplastech.util.*
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

// ==========================================
// CMS DATA MODEL
// ==========================================

data class StatData(val value: String, val label: String)

/**
 *
 * Images:Dimensions/Aspect Ratio: Minimum 800x800px; 4:3 or 1:1 (Square) aspect ratio recommended.
 * Rendering Behavior: Renders with ObjectFit.Cover inside a container with a minimum height of 400px.
 * Text:Overline: Renders at 14px in uppercase. Limit to 2–4 words (e.g., "PASSION FOR EXCELLENCE").
 *
 * Headline: Renders at 40px on desktop and 32px on mobile. Limit to 1–2 lines (approx. 5–8 words).
 * Description: Renders at 18px.
 * Limit to 3–4 sentences (approx. 40–50 words) so it aligns nicely with the height of the adjacent image.
 * Stats Grid: Values render at 32px and labels at 14px. Keep values to 1–4 characters (e.g., "50+", "100%") and labels to 2–3 words.
 *
 */
data class HomeCompanyOverviewData(
    val overline: String,
    val headline: String,
    val description: String,
    val stats: List<StatData>,
    val imagePath: String
)

val defaultCompanyOverviewData = HomeCompanyOverviewData(
    overline = "PASSION FOR EXCELLENCE",
    headline = "Precision Manufacturing at Scale",
    description = "Located in Rajkot, Jexon Plastech is a dedicated manufacturer of all-type homeware quality products. We specialize in durable, high-quality plastic goods including cleaning brushes, floor wipers, brooms, and baskets. Our commitment to excellence ensures reliable, large-scale distribution for vendors globally.",
    stats = listOf(
        StatData("50+", "Products Manufactured"),
        StatData("B2B", "Global Distribution"),
        StatData("100%", "Quality Assured")
    ),
    imagePath = Res.Image.Home.Overview.HOME_OVERVIEW
)

// ==========================================
// COMPONENT
// ==========================================

@Composable
fun HomeCompanyOverviewSection(data: HomeCompanyOverviewData = defaultCompanyOverviewData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()

    val (isVisible, visibilityRef) = rememberScrollVisibility(threshold = 0.2)

    Box(
        modifier = CompanyOverviewContainerStyle.toModifier().padding(
            topBottom = if (isDesktop) 80.px else 40.px, leftRight = 5.percent
        ), contentAlignment = Alignment.Center
    ) {
        Div(
            attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                .justifyContent(com.varabyte.kobweb.compose.css.JustifyContent.Center).toAttrs {
                    ref { element ->
                        visibilityRef(element)
                        onDispose {}
                    }
                }) {
            SimpleGrid(
                numColumns(1, lg = 2),
                modifier = Modifier.fillMaxWidth().maxWidth(1200.px).gap(if (isDesktop) 60.px else 40.px)
            ) {
                // --- LEFT COLUMN: Text & Stats ---
                Column(
                    modifier = Modifier.fillMaxWidth().animateSlideInLeft(isVisible, delayMs = 100),
                    verticalArrangement = Arrangement.Center
                ) {
                    // Overline
                    SpanText(
                        text = data.overline,
                        modifier = Modifier.color(palette.brand.primaryBlue) // Uses your primary brand color
                            .fontSize(14.px).fontWeight(FontWeight.Bold).letterSpacing(1.5.px)
                            .textTransform(TextTransform.Uppercase).margin(bottom = 12.px)
                    )

                    // Headline
                    SpanText(
                        text = data.headline,
                        modifier = Modifier.color(palette.custom.darkText).fontSize(if (isDesktop) 40.px else 32.px)
                            .fontWeight(FontWeight.Bold).lineHeight(1.2).margin(bottom = 24.px)
                    )

                    // Description
                    Div(
                        attrs = Modifier.color(palette.custom.darkText.toRgb().copyf(alpha = 0.8f)).fontSize(18.px)
                            .lineHeight(1.6).margin(bottom = 40.px).toAttrs {
                                ref { htmlElement ->
                                    htmlElement.innerHTML = data.description
                                    onDispose {}
                                }
                            })

                    // Stats Grid (2 columns on all devices for compactness)
                    SimpleGrid(
                        numColumns(2), modifier = Modifier.fillMaxWidth().gap(20.px)
                    ) {
                        data.stats.forEach { stat ->
                            StatCard(stat.value, stat.label)
                        }
                    }
                }

                // --- RIGHT COLUMN: Image ---
                Box(
                    modifier = Modifier.fillMaxWidth().height(100.percent).minHeight(400.px)
                        .animateSlideInRight(isVisible, delayMs = 300), contentAlignment = Alignment.Center
                ) {
                    Image(
                        src = data.imagePath,
                        alt = "Jexon Plastech Manufacturing",
                        modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Cover).borderRadius(16.px)
                            .boxShadow(0.px, 20.px, 40.px, color = palette.custom.darkText.toRgb().copyf(alpha = 0.1f))
                            .unextractable() // Asset protection applied
                    )
                }
            }
        }
    }
}

@Composable
private fun StatCard(value: String, label: String) {
    val palette = ColorMode.current.toSitePalette()

    Column(
        modifier = StatCardStyle.toModifier(), horizontalAlignment = Alignment.Start
    ) {
        SpanText(
            text = value,
            modifier = Modifier.color(palette.brand.primaryBlue).fontSize(32.px).fontWeight(FontWeight.Bold)
                .margin(bottom = 8.px)
        )
        SpanText(
            text = label,
            modifier = Modifier.color(palette.custom.darkText).fontSize(14.px).fontWeight(FontWeight.Medium)
        )
    }
}